import { useRef, useState, createContext, useContext, useCallback } from 'react';
import type { ReactElement, ReactNode, RefObject } from 'react';
import type { TransitionController } from '../../core-transition-component';

export interface FlowProviderContextType {
  start: (
    transitionController: TransitionController,
    crossTransition: boolean,
    release: () => void,
    fromPath: string,
  ) => void;
  transitionOutPromise: Promise<void> | undefined;
  flowHijacked: RefObject<Promise<void> | null>;
  crossFlow: boolean;
  /**
   * Hijacks the transition-in of all pages,
   * could be used for example when we are preloading the entire-app and want to wait with the transition-in of the current route
   */
  hijackFlow: () => void;
  /**
   * This will release the hijacked-flow, page-transition is allowed to start
   */
  releaseHijackFlow: () => void;
}

const FlowProviderContext = createContext<FlowProviderContextType | undefined>(undefined);

export function useFlowProviderContext(): FlowProviderContextType | undefined {
  return useContext(FlowProviderContext);
}

export interface FlowProviderProps {
  children: ReactNode;
}

export function FlowProvider(props: FlowProviderProps): ReactElement {
  const [transitionOutPromise, setTransitionOutPromise] = useState<Promise<void>>();
  const flowHijacked = useRef<Promise<void>>(Promise.resolve());
  const flowHijackResolver = useRef<(_value: void | PromiseLike<void>) => void>(undefined);
  const [crossFlow, setCrossFlow] = useState<boolean>(false);
  const body = useRef(typeof window !== 'undefined' ? document.body : null);

  const releaseHijackFlow = useCallback((): void => {
    if (flowHijackResolver?.current) flowHijackResolver?.current();
  }, [flowHijackResolver]);

  const hijackFlow = useCallback((): void => {
    flowHijacked.current = new Promise<void>((resolver) => {
      flowHijackResolver.current = resolver;
    });
  }, []);

  const disablePointerEvents = useCallback(
    (disable?: boolean) => {
      if (body?.current) {
        body.current.style.pointerEvents = disable ? 'none' : 'all';
      }
    },
    [body],
  );

  const onFlowComplete = useCallback(
    (release: () => void) => {
      release();
      // console.log(new Date().getMilliseconds(), 'release flow');
      disablePointerEvents(false);
    },
    [disablePointerEvents],
  );

  const start = useCallback(
    async (
      transitionController: TransitionController,
      crossTransition: boolean,
      release: () => void,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      fromPath: string,
    ) => {
      disablePointerEvents(true);
      // console.log(new Date().getMilliseconds(), 'start');
      const outPromise = transitionController.transitionOut();
      setCrossFlow(crossTransition);
      setTransitionOutPromise(outPromise);

      // Release flow directly new page/route can be rendered directly
      if (crossTransition) {
        onFlowComplete(release);
      }

      // Wait for transition-out of the leaving-page to be completed.
      await outPromise;
      // console.log(new Date().getMilliseconds(), 'complete');

      // Release flow once page-transition-out is completed to the new page/route can be rendered.
      if (!crossTransition) {
        onFlowComplete(release);
      }
    },
    [onFlowComplete, disablePointerEvents],
  );

  return (
    <FlowProviderContext.Provider
      value={{
        transitionOutPromise,
        crossFlow,
        start,
        flowHijacked,
        hijackFlow,
        releaseHijackFlow,
      }}
    >
      {props.children}
    </FlowProviderContext.Provider>
  );
}
